import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { adminRequest } from "lib/adminSiteApi";
import { hasCapability, useAdmin } from "../RequireAdmin";
import { Badge, Loading } from "../workspace/Controls";
import { date } from "../workspace/Records";
import { titleOf } from "../workspace/catalog";
// Serialize the editor's catalogue reads to avoid bursts against the shared
// authenticated rate bucket. Every request still rechecks staff permissions.
let pendingContentRead = Promise.resolve();
function readContent(path, signal) {
  const request = pendingContentRead.then(() => {
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
    return adminRequest(path, { signal });
  });
  pendingContentRead = request.catch(() => {});
  return request;
}
export function useContentRecords(domain, tick = 0) {
  const [state, setState] = useState({ loading: true, data: [], error: null });
  useEffect(() => {
    const controller = new AbortController();
    setState({ loading: true, data: [], error: null });
    (async () => {
      const records = new Map(),
        seen = new Set();
      let after;
      do {
        const result = await readContent(
          `/admin/cms/${domain}?limit=100${after ? `&after=${encodeURIComponent(after)}` : ""}`,
          controller.signal,
        );
        if (!Array.isArray(result.data?.items))
          throw Error("Content records could not be loaded.");
        result.data.items.forEach((row) => records.set(row.id, row));
        after = result.data.next;
        if (after && seen.has(after))
          throw Error("Content pagination did not finish.");
        if (after) seen.add(after);
      } while (after && !controller.signal.aborted);
      if (!controller.signal.aborted)
        setState({ loading: false, data: [...records.values()], error: null });
    })().catch((error) => {
      if (!controller.signal.aborted)
        setState({ loading: false, data: [], error });
    });
    return () => controller.abort();
  }, [domain, tick]);
  return state;
}
export default function ContentLibrary({ domain, tick, onRefresh }) {
  const profile = useAdmin(),
    state = useContentRecords(domain, tick);
  const [query, setQuery] = useState(""),
    [status, setStatus] = useState(""),
    [category, setCategory] = useState("");
  const rows = useMemo(
    () =>
      state.data
        .filter(
          (row) =>
            (!status || row.status === status) &&
            (!category || row.category === category) &&
            `${titleOf(row)} ${row.slug}`
              .toLowerCase()
              .includes(query.toLowerCase()),
        )
        .sort(
          (a, b) =>
            titleOf(a).localeCompare(titleOf(b)) || a.id.localeCompare(b.id),
        ),
    [state.data, query, status, category],
  );
  return (
    <section className="aw-panel">
      <div className="aw-row">
        <h2>Records</h2>
        <div className="aw-row">
          <button className="aw-button" onClick={onRefresh}>
            Refresh
          </button>
          {hasCapability(profile, "content.edit") && (
            <Link className="aw-button aw-primary" to={`/admin/${domain}/new`}>
              Create new
            </Link>
          )}
        </div>
      </div>
      <div className="aw-filter">
        <label>
          Search name or title
          <input value={query} onChange={(e) => setQuery(e.target.value)} />
        </label>
        <label>
          Status
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {["draft", "published", "archived"].map((value) => (
              <option key={value}>{value}</option>
            ))}
          </select>
        </label>
        {domain === "course-library" && (
          <label>
            Category
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="">All categories</option>
              {["courses", "training", "orientation", "webinars"].map((value) => (
                <option key={value}>{value}</option>
              ))}
            </select>
          </label>
        )}
      </div>
      <Loading state={state}>
        {rows.length ? (
          <div
            className="aw-table-wrap"
            role="region"
            tabIndex={0}
            aria-label="Content records"
          >
            <table>
              <thead>
                <tr>
                  <th>Record</th>
                  <th>Status</th>
                  <th>Homepage</th>
                  <th>Updated</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <th scope="row">
                      <Link
                        className="aw-link"
                        to={`/admin/${domain}/${row.id}`}
                      >
                        {titleOf(row)}
                      </Link>
                      <small>
                        {row.slug}
                        {row.category ? ` · ${row.category}` : ""}
                      </small>
                    </th>
                    <td>
                      <Badge value={row.status} />
                    </td>
                    <td>
                      {row.homepageVisible
                        ? `Visible · ${row.homepageOrder ?? "last"}`
                        : "Hidden"}
                    </td>
                    <td>{date(row.updatedAt)}</td>
                    <td>
                      <Link
                        className="aw-link"
                        to={`/admin/${domain}/${row.id}`}
                      >
                        Open<span className="aw-sr"> {titleOf(row)}</span>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="aw-empty">No records match this view.</p>
        )}
      </Loading>
    </section>
  );
}
